import type React from 'react';
import { useId } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './segmented-control.module.scss';

export interface WordsHappenSegmentedControlOption<Value extends string> {
  value: Value;
  label: React.ReactNode;
}

export interface WordsHappenSegmentedControlProps<Value extends string> {
  /** The group's visible label and accessible name. */
  label: string;
  /** Help text under the control, part of the group's description. */
  hint?: React.ReactNode;
  options: readonly WordsHappenSegmentedControlOption<Value>[];
  /** The selected option. */
  value: Value;
  /** Called with a newly chosen option; never for the one already selected. */
  onChange: (value: Value) => void;
  /** Shows the selected option and blocks choosing another. */
  disabled?: boolean;
  /** Marks the control as busy (saving or loading) with a spinner beside the label, without blocking it. */
  busy?: boolean;
  /** Read out with `busy`, e.g. "Saving". */
  busyLabel?: string;
  /** The radio inputs' name. Defaults to a unique ID. */
  name?: string;
  className?: string;
}

/**
 * One-tap choice between a few options, as a row of segments. A labelled radio group of native radio inputs: Tab
 * enters the group at the selected segment and the arrow keys move between options, choosing as they go.
 *
 * Copied from fiber's ContentSwitcher and reworked from a tab list into a radio group.
 */
export const WordsHappenSegmentedControl = <Value extends string>({
  label,
  hint,
  options,
  value,
  onChange,
  disabled = false,
  busy = false,
  busyLabel = 'Saving',
  name,
  className,
}: WordsHappenSegmentedControlProps<Value>) => {
  const { theme } = useWordsHappenTheme();
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const groupName = name ?? `${id}-options`;

  const cls = [styles.control, theme === 'meadow' ? styles.meadow : styles.flat, className].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className={styles.header}>
        <span id={labelId} className={styles.label}>
          {label}
        </span>
        {busy ? <span className={styles.spinner} role="status" aria-label={busyLabel} /> : null}
      </div>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={hint ? hintId : undefined}
        aria-disabled={disabled || undefined}
        aria-busy={busy || undefined}
        className={styles.track}
      >
        {options.map((option) => (
          <label key={option.value} className={styles.segment}>
            <input
              type="radio"
              className={styles.input}
              name={groupName}
              value={option.value}
              checked={option.value === value}
              disabled={disabled}
              onChange={() => {
                if (option.value !== value) {
                  onChange(option.value);
                }
              }}
            />
            <span className={styles.face}>{option.label}</span>
          </label>
        ))}
      </div>
      {hint ? (
        <div id={hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
    </div>
  );
};
